import { Form, Formik, FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../Session';
import {
    DeviceSettingsType,
    GetMessageTemplateSettingsDocument,
    GetSystemSettingDocument,
    TemplateUseCases,
    useDisablePushMutation,
    useEnablePushMutation,
    useGetMessageTemplateSettingsQuery,
    useGetSingleSignInTokenMutation,
    useGetSystemSettingQuery,
    UserType,
    useToggleBiometricMutation,
    useUpdateMessageTemplateSettingsMutation,
    useUpdateSystemSettingMutation,
} from '../../api';
import { useAlert } from '../../components/AlertProvider';
import { useFlutter } from '../../components/FlutterProvider';
import { useLoadingController } from '../../components/LoadingProvider';
import useValidator from '../../utilities/useValidator';
import validators from '../../utilities/validators';
import { SettingsHeader, SettingsBody } from './components';
import type { FormValues } from './shared';

type MessageTemplateFieldName =
    | 'upcomingBidTemplateUseCase'
    | 'biddingOpenedTemplateUseCase'
    | 'bidSubmissionTemplateUseCase'
    | 'highestBidSubmissionTemplateUseCase'
    | 'noChancesLeftTemplateUseCase'
    | 'bidAwardedTemplateUseCase'
    | 'bidAwardedForSalesConsultantTemplateUseCase'
    | 'otpTemplateUseCase'
    | 'userAccountRejectionTemplateUseCase'
    | 'userAccountApprovalTemplateUseCase';

const messageTemplateFields: Array<{ field: MessageTemplateFieldName; useCase: TemplateUseCases }> = [
    { field: 'upcomingBidTemplateUseCase', useCase: TemplateUseCases.UpcomingBid },
    { field: 'biddingOpenedTemplateUseCase', useCase: TemplateUseCases.BiddingOpened },
    { field: 'bidSubmissionTemplateUseCase', useCase: TemplateUseCases.BidSubmission },
    { field: 'highestBidSubmissionTemplateUseCase', useCase: TemplateUseCases.HighestBidSubmission },
    { field: 'noChancesLeftTemplateUseCase', useCase: TemplateUseCases.NoChancesLeft },
    { field: 'bidAwardedTemplateUseCase', useCase: TemplateUseCases.BidAwarded },
    {
        field: 'bidAwardedForSalesConsultantTemplateUseCase',
        useCase: TemplateUseCases.BidAwardedForSalesConsultant,
    },
    { field: 'otpTemplateUseCase', useCase: TemplateUseCases.Otp },
    { field: 'userAccountRejectionTemplateUseCase', useCase: TemplateUseCases.UserAccountRejection },
    { field: 'userAccountApprovalTemplateUseCase', useCase: TemplateUseCases.UserAccountApproval },
];

const Settings = () => {
    const { t } = useTranslation(['settingsPage']);
    const { show } = useAlert();
    const { deviceFingerPrint, checkBiometric, initializePush } = useFlutter();

    const user = useCurrentUser();
    const { deviceSettings } = user;

    const { data: messageTemplateSettingsData, loading: messageTemplateSettingsLoading } =
        useGetMessageTemplateSettingsQuery();
    const { data: systemSettingData, loading: systemSettingLoading } = useGetSystemSettingQuery();

    const [toggleBiometric, { loading: toggleBiometricLoading }] = useToggleBiometricMutation();
    const [getSingleSignInToken, { loading: singleSignInTokenLoading }] = useGetSingleSignInTokenMutation();
    const [enablePush, { loading: enablePushLoading }] = useEnablePushMutation();
    const [disablePush, { loading: disablePushLoading }] = useDisablePushMutation();
    const [updateMessageTemplateSettings, { loading: updateMessageTemplateSettingsLoading }] =
        useUpdateMessageTemplateSettingsMutation();
    const [updateSystemSetting, { loading: updateSystemSettingLoading }] = useUpdateSystemSettingMutation();

    useLoadingController(
        messageTemplateSettingsLoading ||
            systemSettingLoading ||
            toggleBiometricLoading ||
            singleSignInTokenLoading ||
            enablePushLoading ||
            disablePushLoading ||
            updateMessageTemplateSettingsLoading ||
            updateSystemSettingLoading
    );

    const useCases = useMemo(() => messageTemplateSettingsData?.messageTemplateSettings, [messageTemplateSettingsData]);

    const getTemplateSid = useCallback(
        (useCase: TemplateUseCases) =>
            useCases?.find(setting => setting.useCase === useCase)?.selectedTemplateSid ?? '',
        [useCases]
    );

    const currentUser = useCurrentUser();

    const formValidator = validators.compose(
        validators.only(
            () => currentUser.type === UserType.Admin || currentUser.type === UserType.Root,
            validators.compose(
                validators.requiredString('systemContactEmail'),
                validators.validEmail('systemContactEmail')
            )
        )
    );

    const validate = useValidator<FormValues>(formValidator);

    const initialValues = useMemo((): FormValues => {
        const find = (type: DeviceSettingsType) =>
            deviceSettings.some(setting => setting.type === type && setting.deviceFingerprint === deviceFingerPrint);

        return {
            biometric: find(DeviceSettingsType.Biometric),
            pushNotification: find(DeviceSettingsType.Push),
            systemContactEmail: systemSettingData?.systemSetting.contactEmail ?? '',
            biddingOpenedTemplateUseCase: getTemplateSid(TemplateUseCases.BiddingOpened),
            upcomingBidTemplateUseCase: getTemplateSid(TemplateUseCases.UpcomingBid),
            bidSubmissionTemplateUseCase: getTemplateSid(TemplateUseCases.BidSubmission),
            highestBidSubmissionTemplateUseCase: getTemplateSid(TemplateUseCases.HighestBidSubmission),
            noChancesLeftTemplateUseCase: getTemplateSid(TemplateUseCases.NoChancesLeft),
            bidAwardedTemplateUseCase: getTemplateSid(TemplateUseCases.BidAwarded),
            bidAwardedForSalesConsultantTemplateUseCase: getTemplateSid(TemplateUseCases.BidAwardedForSalesConsultant),
            otpTemplateUseCase: getTemplateSid(TemplateUseCases.Otp),
            userAccountRejectionTemplateUseCase: getTemplateSid(TemplateUseCases.UserAccountRejection),
            userAccountApprovalTemplateUseCase: getTemplateSid(TemplateUseCases.UserAccountApproval),
        };
    }, [deviceFingerPrint, deviceSettings, getTemplateSid, systemSettingData?.systemSetting.contactEmail]);

    const handleSubmit = useCallback(
        async (values: FormValues, { resetForm, setFieldValue }: FormikHelpers<FormValues>) => {
            const nextValues = { ...values };
            let hasSaved = false;

            if (values.biometric !== initialValues.biometric) {
                if (values.biometric) {
                    const biometricVerified = await checkBiometric();

                    if (!biometricVerified) {
                        nextValues.biometric = false;
                        await setFieldValue('biometric', false, false);
                    } else {
                        await toggleBiometric({ variables: { deviceFingerprint: deviceFingerPrint, state: true } });

                        const response = await getSingleSignInToken({
                            variables: { deviceFingerprint: deviceFingerPrint },
                        });
                        const token = response.data.getSingleSignInToken;

                        localStorage.setItem('singleSignIn', token);
                        hasSaved = true;
                    }
                } else {
                    await toggleBiometric({ variables: { deviceFingerprint: deviceFingerPrint, state: false } });
                    localStorage.removeItem('singleSignIn');
                    hasSaved = true;
                }
            }

            if (nextValues.pushNotification !== initialValues.pushNotification) {
                if (nextValues.pushNotification) {
                    const { registrationToken } = await initializePush();

                    if (!registrationToken) {
                        nextValues.pushNotification = false;
                        await setFieldValue('pushNotification', false, false);
                    } else {
                        await enablePush({ variables: { registrationToken, deviceFingerprint: deviceFingerPrint } });
                        hasSaved = true;
                    }
                } else {
                    await disablePush({ variables: { deviceFingerprint: deviceFingerPrint } });
                    hasSaved = true;
                }
            }

            if (user.type === UserType.Admin || user.type === UserType.Root) {
                // only Root user can edit message templates settings
                if (user.type === UserType.Root) {
                    const messageTemplateChanged = messageTemplateFields.some(
                        ({ field }) => values[field] !== initialValues[field]
                    );

                    if (messageTemplateChanged) {
                        await updateMessageTemplateSettings({
                            variables: {
                                useCaseSettings: messageTemplateFields.map(({ field, useCase }) => ({
                                    useCase,
                                    selectedTemplateSid: values[field] || null,
                                })),
                            },
                            refetchQueries: [{ query: GetMessageTemplateSettingsDocument }],
                        });

                        hasSaved = true;
                    }
                }

                if (values.systemContactEmail !== initialValues.systemContactEmail) {
                    await updateSystemSetting({
                        variables: {
                            fields: {
                                contactEmail: values.systemContactEmail,
                            },
                        },
                        refetchQueries: [{ query: GetSystemSettingDocument }],
                    });

                    hasSaved = true;
                }
            }

            resetForm({ values: nextValues });

            if (hasSaved) {
                show('success', t('settingsPage:alerts.saveSuccessful'));
            }
        },
        [
            checkBiometric,
            deviceFingerPrint,
            disablePush,
            enablePush,
            getSingleSignInToken,
            initialValues,
            initializePush,
            show,
            t,
            toggleBiometric,
            updateMessageTemplateSettings,
            updateSystemSetting,
            user.type,
        ]
    );

    // Render Formik only after data exists
    // to prevent users from seeing the form values being populated
    // while initialValues are not set yet
    if (!useCases || !systemSettingData?.systemSetting) {
        return null;
    }

    return (
        <Formik
            key={deviceFingerPrint || 'none'}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validate={validate}
        >
            {() => (
                <Form>
                    <SettingsHeader />
                    <SettingsBody />
                </Form>
            )}
        </Formik>
    );
};

export default Settings;
